import React, { FC } from 'react';
import styled from 'styled-components';
import { LIBX_SORT_BY } from '../../../shared/common/SortByDropDown';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../../shared/components/NoElementsFound';
import ProductList from '../../../shared/common/ProductList';
import { Pagination } from '../../../shared/common/Pagination';
import { FilterBar } from '../../../shared/common/FilterBar';
import LibxchangeProductElement from './LibxchangeProductElement';
import ContentBaseLayout from './ContentBaseLayout';
import { useAddLibxProductToCollection } from '../productdetail/api/useAddLibxProductToCollection';
import { invalidateNewlyAddedProducts } from '../api/useNewlyAddedProducts';
import { ProductOut } from '../../../store/api';
import { invalidateAllTitlesProducts } from '../api/useAllTitlesProducts';
import { AggregationsProps, ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { DISPLAY_TYPE_TILE, DisplayType } from '../../../shared/common/displayType/types';
import { useDisplayFilterSearchParam } from '../../../shared/utils/useDisplayFilterSearchParam';

const StFilterBar = styled(FilterBar)`
    margin: 10px 0 40px 0;
`;

type Props = AggregationsProps & {
    products: {
        count: number;
        list: ProductOut[];
    };
    loading: boolean;
    documentTitle: string;
    displayType: DisplayType;
    pageSize: number;
    sortBy: string;
    page: number;
    isNewlyAdded: boolean;
    isAllTitles: boolean;
    shopLanguage: string | undefined;
};

export const LibxChangeProductPageBuilder: FC<Props> = props => {
    const { displayFilter } = useDisplayFilterSearchParam();

    const { mutate: addToCollectionMutation } = useAddLibxProductToCollection();
    const { products, loading, documentTitle, displayType, pageSize, isNewlyAdded, isAllTitles, shopLanguage } = props;

    function onAddToCollection(productId: string) {
        addToCollectionMutation(productId, {
            onSuccess: () => {
                if (isNewlyAdded) {
                    invalidateNewlyAddedProducts().then();
                }
                if (isAllTitles) {
                    invalidateAllTitlesProducts().then();
                }
            }
        });
    }

    return (
        <ContentBaseLayout
            topComponent={
                <StFilterBar
                    totalProducts={products.count}
                    hideGroupBy={true}
                    hideSelectButtons={true}
                    excludeDisplayTypes={[DISPLAY_TYPE_TILE]}
                    sorting={LIBX_SORT_BY}
                    displayType={displayType}
                    pageSize={pageSize}
                    sortBy={props.sortBy}
                />
            }
            sidebarComponent={
                displayFilter ? (
                    <ResponsiveFilterBar
                        visibleFilterGroups={props.visibleFilterGroups}
                        buildQueryKey={props.buildQueryKey}
                        fetchAggregations={props.fetchAggregations}
                        fetchAggregationsQuickSearch={props.fetchAggregationsQuickSearch}
                    />
                ) : null
            }
            contentComponent={
                products.list.length === 0 && !props.loading ? (
                    <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                ) : (
                    <div className="ca-main">
                        <div className="ca-main-inner">
                            <ProductList products={products.list} displayType={displayType} onAddPrice={async () => {}}>
                                {({ key, isListView, ...value }) => (
                                    <LibxchangeProductElement
                                        {...value}
                                        key={key}
                                        displayType={displayType}
                                        onAddToCollection={onAddToCollection}
                                        shopLanguage={shopLanguage}
                                    />
                                )}
                            </ProductList>
                            <Pagination totalItems={products.count} pageSize={pageSize} />
                        </div>
                    </div>
                )
            }
            documentTitle={documentTitle}
            loading={loading}
        />
    );
};
